import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  faGraduationCap,
  faShieldAlt,
  faTrophy,
  faBlog,
  faBookOpen,
  faUsers,
  faRobot,
  faChartLine,
  faComments,
  faVideo,
  faCertificate,
  faCheckCircle,
  faArrowLeft,
  faClock,
  faUserGraduate,
  faChalkboardTeacher,
  faHeart,
  faLightbulb,
  faRocket,
  faShare,
  faPrint,
  faBullhorn,
  faClone,
  faBrain,
  faBook,
  faFilePdf,
  faSearch,
  faMagic
} from '@fortawesome/free-solid-svg-icons';
import RafiqChatBot from '../Shared/RafiqChatBot';
import Header from "../Shared/Header";
import Footer from "../Shared/Footer";
import botImage from '../../assets/images/bot.png';

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const OurServices = () => {
  const [activeTab, setActiveTab] = useState('exams');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['exams', 'blogs', 'courses', 'rafiq', 'mozakara'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
    if (serviceId && ['exams', 'blogs', 'courses', 'rafiq', 'mozakara'].includes(serviceId)) {
      setActiveTab(serviceId);
    }
  }, [serviceId, searchParams]);

  const services = {
    exams: {
      id: "exams",
      title: "الامتحانات التفاعلية الآمنة",
      subtitle: "منصة امتحانات ذكية مع حماية متقدمة ضد الغش",
      icon: faGraduationCap,
      color: "#6366f1",
      features: [
        { icon: faShieldAlt, title: "حماية ضد الغش", desc: "نظام ذكي يمنع تبديل النوافذ ويراقب الشاشة." },
        { icon: faTrophy, title: "لوحة الشرف", desc: "تنافس مع زملائك واظهر في قائمة المتفوقين." },
        { icon: faClock, title: "نتائج فورية", desc: "تصحيح تلقائي ونتائج تظهر فور انتهاء الامتحان." },
        { icon: faRobot, title: "ذكاء اصطناعي", desc: "توليد أسئلة ذكية ومتنوعة بضغطة زر." },
        { icon: faChartLine, title: "تحليل الأداء", desc: "رسوم بيانية توضح مستوى تقدم الطالب." },
        { icon: faPrint, title: "طباعة ورقية", desc: "إمكانية طباعة الامتحانات ونماذج الإجابة." },
      ]
    },
    blogs: {
      id: "blogs",
      title: "المدونات والتفاعل الاجتماعي",
      subtitle: "مجتمع تعليمي متكامل لتبادل المعرفة والخبرات",
      icon: faBlog,
      color: "#ec4899",
      features: [
        { icon: faComments, title: "تفاعل مستمر", desc: "نظام تعليقات وإعجابات لتبادل الآراء." },
        { icon: faUserGraduate, title: "مقالات تعليمية", desc: "محتوى غني يكتبه نخبة من المعلمين والطلاب." },
        { icon: faShare, title: "مشاركة المعرفة", desc: "سهولة مشاركة المحتوى المفيد مع الآخرين." },
        { icon: faHeart, title: "تفضيلات المحتوى", desc: "حفظ المقالات المفضلة للعودة إليها لاحقاً." },
        { icon: faLightbulb, title: "أفكار ملهمة", desc: "اكتشف طرق مذاكرة جديدة ونصائح قيمة." },
      ]
    },
    courses: {
      id: "courses",
      title: "الدورات التدريبية",
      subtitle: "رحلة تعلم شاملة مع أفضل المعلمين والمحتوى المتميز",
      icon: faBookOpen,
      color: "#10b981",
      features: [
        { icon: faVideo, title: "دروس مسجلة", desc: "محتوى فيديو عالي الجودة متاح في أي وقت." },
        { icon: faCertificate, title: "شهادات معتمدة", desc: "احصل على شهادة إتمام بعد اجتياز الدورة." },
        { icon: faUsers, title: "متابعة الطلاب", desc: "أدوات متقدمة للمعلم لمتابعة تقدم طلابه." },
        { icon: faBullhorn, title: "تسويق للدورات", desc: "منصة لعرض دوراتك والوصول لعدد أكبر من الطلاب." },
        { icon: faRocket, title: "تطور مستمر", desc: "مناهج محدثة تواكب أحدث التطورات التعليمية." },
      ]
    },
    rafiq: {
      id: "rafiq",
      title: "رفيق - مساعدك الذكي",
      subtitle: "ذكاء اصطناعي متطور لخدمة الطالب والمعلم على مدار الساعة",
      icon: faRobot,
      color: "#8b5cf6",
      features: [
        { icon: faBrain, title: "تلخيص ذكي", desc: "تلخيص للمحتوى الدراسي الطويل في نقاط موجزة وسهلة الفهم." },
        { icon: faClone, title: "بطاقات تعليمية", desc: "تحويل الدروس إلى بطاقات ذاكرة للمراجعة السريعة والفعالة." },
        { icon: faComments, title: "محادثة تفاعلية", desc: "إجابات فورية ودقيقة على جميع استفساراتك الدراسية في أي وقت." },
        { icon: faChalkboardTeacher, title: "مساعد المعلم", desc: "أدوات ذكية لمساعدة المعلم في تحضير الدروس وإنشاء الامتحانات." },
        { icon: faRocket, title: "مسار تعلم مخصص", desc: "توصيات ذكية وخطط دراسية تناسب مستوى وأهداف كل طالب." },
        { icon: faLightbulb, title: "مستشار أكاديمي", desc: "تقديم نصائح دراسية وخطط زمنية مخصصة لتحسين الأداء العام وتجاوز الصعوبات." },
      ]
    },
    mozakara: {
      id: "mozakara",
      title: "المذكرات والملخصات الدراسية",
      subtitle: "مكتبة رقمية متكاملة لتبادل وتحميل المذكرات والملخصات الدراسية",
      icon: faBook,
      color: "#059669",
      features: [
        { icon: faFilePdf, title: "ملفات PDF منظمة", desc: "تصفح وتحميل المذكرات والملخصات بصيغة PDF عالية الجودة." },
        { icon: faSearch, title: "بحث سريع وتصفية", desc: "ابحث عن مذكراتك المفضلة وسهل وصولك إليها حسب المادة والصف." },
        { icon: faClock, title: "تحديثات مستمرة", desc: "إضافة أحدث المذكرات ونماذج الامتحانات المحدثة باستمرار." },
        { icon: faUsers, title: "مشاركة وتكامل", desc: "تحميل وعرض المذكرات مباشرة لدعم استذكار الدروس بكفاءة عالية." },
        { icon: faMagic, title: "عروض تقديمية AI", desc: "أنشئ عروضاً تقديمية (PowerPoint) احترافية مدعومة بالذكاء الاصطناعي بسهولة." },
      ]
    }
  };

  const initialChatOptions = [
    { text: "ما هي خدمات الامتحانات؟", value: "exams" },
    { text: "ما هي خدمة المدونات؟", value: "blogs" },
    { text: "ما هي خدمة الدورات؟", value: "courses" },
    { text: "من هو رفيق؟", value: "rafiq" },
    { text: "ما هي خدمة المذكرات؟", value: "mozakara" }
  ];

  const handleChatOptionSelect = async (option) => {
    if (option.value === 'back') return { nextOptions: initialChatOptions, message: 'كيف يمكنني مساعدتك أيضاً؟' };
    if (services[option.value]) {
      const service = services[option.value];
      return {
        message: `${service.title}: ${service.subtitle}`,
        nextOptions: [{ text: 'عودة للقائمة', value: 'back' }]
      };
    }
    return { message: "أهلاً بك في منصة امتحاني!", nextOptions: initialChatOptions };
  };

  return (
    <div className="os-root" dir="rtl">
      <Header />

      {/* Hero Section */}
      <section className="os-hero">
        <div className="os-container">
          <motion.div
            className="os-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="os-eyebrow" variants={fadeUp} custom={0}>
              <span className="os-eyebrow-dot" />
              خدماتنا المتكاملة
            </motion.div>
            <motion.h2 className="os-title" variants={fadeUp} custom={1}>
              حلول تعليمية <em>ذكية ومبتكرة</em>
            </motion.h2>
            <motion.p className="os-subtitle" variants={fadeUp} custom={2}>
              نقدم مجموعة واسعة من الخدمات المصممة خصيصاً لرفع كفاءة التعليم وتمكين الطلاب والمعلمين من تحقيق التميز.
            </motion.p>
          </motion.div>

          <motion.div
            className="os-tabs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="os-tabs-wrapper">
              {Object.values(services).map((s) => (
                <button
                  key={s.id}
                  className={`os-tab-btn ${activeTab === s.id ? 'is-active' : ''}`}
                  onClick={() => setActiveTab(s.id)}
                  style={{ '--active-color': s.color }}
                >
                  <FontAwesomeIcon icon={s.icon} />
                  <span>{s.title.split(' ')[0]}</span>
                  {activeTab === s.id && <motion.div className="os-tab-indicator" layoutId="osTab" />}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="os-section">
        <div className="os-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="os-detail-card"
            >
              <div className="os-card-head">
                <div
                  className="os-card-icon"
                  style={{ background: `${services[activeTab].color}10`, color: services[activeTab].color }}
                >
                  <FontAwesomeIcon icon={services[activeTab].icon} />
                </div>
                <div className="os-card-info">
                  <h2 className="os-card-title">{services[activeTab].title}</h2>
                  <p className="os-card-subtitle">{services[activeTab].subtitle}</p>
                </div>
              </div>

              <div className="os-features-grid">
                {services[activeTab].features.map((f, idx) => (
                  <motion.div
                    key={idx}
                    className="os-feature-item"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={idx}
                    whileHover={{ y: -5 }}
                  >
                    <div className="os-feat-icon" style={{ color: services[activeTab].color }}>
                      <FontAwesomeIcon icon={f.icon} />
                    </div>
                    <h3 className="os-feat-title">{f.title}</h3>
                    <p className="os-feat-desc">{f.desc}</p>
                  </motion.div>
                ))}
              </div>

              <div className="os-card-footer">
                <motion.button
                  className="os-btn-main"
                  style={{ background: services[activeTab].color }}
                  onClick={() => {
                    if (activeTab === 'mozakara') {
                      navigate('/mozakara');
                    } else if (activeTab === 'exams') {
                      navigate('/myexam');
                    } else if (activeTab === 'courses') {
                      navigate('/mycourses');
                    } else if (activeTab === 'blogs') {
                      navigate('/blogs');
                    } else if (activeTab === 'rafiq') {
                      navigate('/zakerly');
                    } else {
                      navigate('/register-teacher');
                    }
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>استكشف الخدمة الآن</span>
                  <FontAwesomeIcon icon={faArrowLeft} />
                </motion.button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <RafiqChatBot
        botName="رفيق"
        botImage={botImage}
        initialMessage="أهلاً بك! أنا رفيق، مساعدك الذكي في منصة امتحاني. كيف يمكنني مساعدتك اليوم؟"
        options={initialChatOptions}
        onOptionSelect={handleChatOptionSelect}
        isOpen={isChatOpen}
        setIsOpen={setIsChatOpen}
      />

      <Footer />

      <style jsx>{`
        .os-root {
          font-family: "Zain", sans-serif;
          background: #fff;
          min-height: 100vh;
          direction: rtl;
        }

        .os-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .os-section { padding: 80px 0 120px; }

        /* ── Header ── */
        .os-header { text-align: center; margin-bottom: 60px; }
        .os-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.85rem;
          letter-spacing: 0.14em; text-transform: uppercase;
          margin-bottom: 24px;
        }
        .os-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: os-pulse 2.2s ease-in-out infinite;
        }
        @keyframes os-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
          50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }
        .os-title {
          font-size: clamp(2.4rem, 4.5vw, 4rem);
          font-weight: 900; color: #0f172a; margin: 0 0 20px;
          line-height: 1.18; letter-spacing: -0.025em;
        }
        .os-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 60%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .os-subtitle {
          font-size: 1.2rem; color: #64748b; line-height: 1.85; margin: 0 auto;
          max-width: 700px; font-weight: 500;
        }

        /* ── Hero & Tabs ── */
        .os-hero { padding: 160px 0 40px; background: #fcfcfd; border-bottom: 1px solid #f1f5f9; }
        .os-tabs { display: flex; justify-content: center; margin-top: 40px; }
        .os-tabs-wrapper {
          display: flex; gap: 12px; background: #fff; padding: 8px;
          border-radius: 100px; border: 1.5px solid #f1f5f9; box-shadow: 0 4px 15px rgba(0,0,0,0.02);
        }
        .os-tab-btn {
          position: relative; display: flex; align-items: center; gap: 10px;
          padding: 12px 28px; border: none; background: transparent;
          color: #94a3b8; font-family: inherit; font-weight: 800; font-size: 1.1rem;
          cursor: pointer; transition: 0.3s;
        }
        .os-tab-btn:hover { color: #475569; }
        .os-tab-btn.is-active { color: var(--active-color); }
        .os-tab-indicator {
          position: absolute; inset: 0; background: color-mix(in srgb, var(--active-color), transparent 92%);
          border: 1.5px solid var(--active-color); border-radius: 100px; z-index: 0;
        }
        .os-tab-btn span, .os-tab-btn svg { position: relative; z-index: 1; }

        /* ── Detail Card ── */
        .os-detail-card {
          background: #fff; border: 1.5px solid rgba(15,23,42,0.06);
          border-radius: 40px; padding: 60px; box-shadow: 0 20px 50px rgba(0,0,0,0.03);
        }
        .os-card-head { display: flex; align-items: center; gap: 32px; margin-bottom: 48px; padding-bottom: 40px; border-bottom: 1px dashed #f1f5f9; }
        .os-card-icon {
          width: 80px; height: 80px; border-radius: 24px; display: flex;
          align-items: center; justify-content: center; font-size: 2.2rem; flex-shrink: 0;
        }
        .os-card-title { font-size: 2.2rem; font-weight: 900; color: #0f172a; margin: 0 0 8px; }
        .os-card-subtitle { font-size: 1.15rem; color: #64748b; font-weight: 500; }

        .os-features-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
        .os-feature-item {
          padding: 32px; background: #fcfcfd; border-radius: 24px; border: 1.5px solid transparent; transition: 0.3s;
        }
        .os-feature-item:hover { background: #fff; border-color: #f1f5f9; box-shadow: 0 10px 30px rgba(0,0,0,0.03); }
        .os-feat-icon { font-size: 1.6rem; margin-bottom: 20px; }
        .os-feat-title { font-size: 1.3rem; font-weight: 800; color: #1e293b; margin-bottom: 12px; }
        .os-feat-desc { font-size: 1rem; color: #64748b; line-height: 1.7; font-weight: 500; }

        .os-card-footer { text-align: center; margin-top: 56px; }
        .os-btn-main {
          display: inline-flex; align-items: center; gap: 12px;
          padding: 18px 48px; border-radius: 100px; border: none;
          color: #fff; font-family: inherit; font-weight: 900; font-size: 1.15rem;
          cursor: pointer; box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        @media (max-width: 1024px) {
          .os-detail-card { padding: 40px; }
          .os-card-head { flex-direction: column; text-align: center; gap: 20px; }
        }

        @media (max-width: 768px) {
          .os-tabs-wrapper { flex-wrap: wrap; justify-content: center; border-radius: 24px; padding: 12px; }
          .os-tab-btn { padding: 10px 20px; font-size: 1rem; }
          .os-card-title { font-size: 1.8rem; }
          .os-detail-card { padding: 30px 20px; }
        }
      `}</style>
    </div>
  );
};

export default OurServices;